/**
 * Signup Modal
 * Handles new user registration flow for BSG API Hub
 */

import { ToasterService } from '@/services/ToasterService';
import { ModalSystem } from '@/services/ModalSystem';
import { loadOEMTemplate } from '@/services/OEMUtils';
import { Logger } from '@/services/Logger';
import { getSiteKey } from '@/services/api/TriSysWebAPI';
import { auth0Service } from '@/services/Auth0Service';

const logger = Logger.scope('Signup');

// Stores the Auth0 claims base64 from social login during signup
let socialProfileBase64: string | null = null;

/**
 * Show the signup/request access modal
 */
export async function showSignupModal(): Promise<void> {
  try {
    const html = await loadOEMTemplate('/forms/auth/signup.html');

    ModalSystem.show({
      title: 'Create Account',
      html,
      width: 'lg',
      onOpened: () => {
        setupSignupForm();
      }
    });
  } catch (error) {
    logger.error('Error loading signup form:', error);
    ToasterService.error('Failed to load signup form');
  }
}

/**
 * Setup the signup form submission handler
 */
function setupSignupForm(): void {
  const form = document.getElementById('signup-form') as HTMLFormElement;
  if (!form) {
    logger.warn('Signup form not found');
    return;
  }

  // Handle "Sign in" link - just close the modal
  const signInLink = document.getElementById('signup-signin-link');
  logger.debug('Sign in link found:', !!signInLink);
  if (signInLink) {
    signInLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      logger.debug('Sign in link clicked - closing modal');
      ModalSystem.hide();
    });
  } else {
    logger.warn('Signup sign-in link not found');
  }

  // Handle Terms and Privacy links - open in new tab without status bar display
  const termsLink = document.getElementById('terms-link');
  const privacyLink = document.getElementById('privacy-link');
  
  [termsLink, privacyLink].forEach(link => {
    if (link) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const url = link.getAttribute('data-url');
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      });
    }
  });

  // Handle Social Login button
  socialProfileBase64 = null;
  const socialBtn = document.getElementById('signup-social-btn');
  socialBtn?.addEventListener('click', async () => {
    try {
      logger.debug('Social signup clicked — opening Auth0 popup');
      const auth0User = await auth0Service.login();

      // Show progress
      (socialBtn as HTMLButtonElement).classList.add('loading');
      (socialBtn as HTMLButtonElement).disabled = true;

      // POST auth token to apiHub/LoginToSocialNetworkDuringCreateAccount
      const apiBase = sessionStorage.getItem('trisys_api_endpoint') || 'https://api.trisys.co.uk';
      const siteKey = getSiteKey();
      const claimsToSend = auth0User.rawClaims || auth0User;
      const auth0Base64 = btoa(JSON.stringify(claimsToSend));

      const response = await fetch(`${apiBase}/APIhub/LoginToSocialNetworkDuringCreateAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'SiteKey': siteKey
        },
        body: JSON.stringify({ Auth0UserObjectBase64: auth0Base64 })
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);
      const result = await response.json();

      if (result.Success) {
        // If DataServicesKey returned, account already exists — log them in directly
        if (result.DataServicesKey) {
          logger.info('Existing subscriber detected — logging in via social', { email: auth0User.email });
          sessionStorage.setItem('trisys_data_services_key', result.DataServicesKey);

          ToasterService.dismissAll();
          const providerName = auth0Service.getProviderName(auth0User.provider);
          ToasterService.success(`Welcome back, ${auth0User.name || auth0User.email}!`);

          // Reload the app — it will find the valid session key and bootstrap fully
          ModalSystem.hide();
          window.location.hash = '';
          window.location.reload();
          return;
        }

        // New account — store the GUID returned by the API for registration
        socialProfileBase64 = result.GUID || null;
        logger.debug('Social signup pre-auth successful', { guid: socialProfileBase64 });

        // Populate Full Name and Email from Auth0 data
        const nameInput = document.getElementById('signup-name') as HTMLInputElement;
        const emailInput = document.getElementById('signup-email') as HTMLInputElement;
        if (nameInput && auth0User.name) { nameInput.value = auth0User.name; nameInput.readOnly = true; }
        if (emailInput && auth0User.email) { emailInput.value = auth0User.email; emailInput.readOnly = true; }

        // Populate Company Name if returned by the API
        if (result.CompanyName) {
          const companyInput = document.getElementById('signup-company') as HTMLInputElement;
          if (companyInput) {
            companyInput.value = result.CompanyName;
            companyInput.readOnly = true;
            companyInput.removeAttribute('required');
          }
        }

        // Populate phone numbers if returned by the API
        if (result.WorkTelNo) {
          const workTelInput = document.getElementById('signup-work-tel') as HTMLInputElement;
          if (workTelInput) {
            workTelInput.value = result.WorkTelNo;
            workTelInput.readOnly = true;
            workTelInput.removeAttribute('required');
          }
        }
        if (result.MobileTelNo) {
          const mobileInput = document.getElementById('signup-mobile') as HTMLInputElement;
          if (mobileInput) {
            mobileInput.value = result.MobileTelNo;
            mobileInput.readOnly = true;
          }
        }

        // Hide password fields and remove required so browser validation doesn't block submit
        const pwSection = document.getElementById('signup-password-section');
        if (pwSection) {
          pwSection.classList.add('hidden');
          pwSection.querySelectorAll('input[required]').forEach(input => input.removeAttribute('required'));
        }

        // Update button to show connected state — remove loading first to avoid layout issues
        const providerName = auth0Service.getProviderName(auth0User.provider);
        const providerIcon = auth0Service.getProviderIcon(auth0User.provider);
        (socialBtn as HTMLButtonElement).classList.remove('loading');
        (socialBtn as HTMLButtonElement).disabled = false;
        socialBtn.className = 'btn btn-success w-full gap-2';
        socialBtn.innerHTML = `<i class="${providerIcon}"></i> Connected to ${providerName}`;

        ToasterService.success(`Authenticated with ${providerName}`);
      } else {
        ToasterService.error(result.ErrorMessage || 'Social authentication failed');
      }
    } catch (error: any) {
      logger.warn('Social signup flow interrupted', { error: error.message });
    } finally {
      (socialBtn as HTMLButtonElement).classList.remove('loading');
      (socialBtn as HTMLButtonElement).disabled = false;
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    
    const fullName = (formData.get('fullName') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const companyName = (formData.get('companyName') as string || '').trim();
    const workTelNo = (formData.get('workTelNo') as string || '').trim();
    const mobileTelNo = (formData.get('mobileTelNo') as string || '').trim();
    const password = formData.get('password') as string || '';
    const confirmPassword = formData.get('confirmPassword') as string || '';

    // Validate required fields (companyName/workTelNo not required if pre-filled from social login)
    if (!fullName || !email) {
      ToasterService.warning('Please fill in all required fields');
      return;
    }
    if (!workTelNo && !socialProfileBase64) {
      ToasterService.warning('Please fill in all required fields');
      return;
    }
    if (!companyName && !socialProfileBase64) {
      ToasterService.warning('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      ToasterService.warning('Please enter a valid email address');
      return;
    }

    // Only validate password if not using social login
    if (!socialProfileBase64) {
      if (password.length < 6) {
        ToasterService.warning('Password must be at least 6 characters');
        return;
      }

      if (password !== confirmPassword) {
        ToasterService.warning('Passwords do not match');
        return;
      }
    }

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Creating account...';

    try {
      // Call TriSys Web API to register
      const apiBase = sessionStorage.getItem('trisys_api_endpoint') || 'https://api.trisys.co.uk';
      logger.debug('Registering new account', { email, companyName });

      const payload: Record<string, string> = {
          FullName: fullName,
          Company: companyName,
          EMail: email,
          TelNo: workTelNo || mobileTelNo,
          Street: '',
          City: '',
          County: '',
          PostCode: '',
          Country: '',
          Password: socialProfileBase64 ? '' : password,
          TypeOfRequest: 'Pay-As-You-Go',
          ApplicationName: 'API-Hub'
        };

      if (socialProfileBase64) {
        payload.SocialNetworkLoginProfileBase64 = socialProfileBase64;
      }

      const response = await fetch(`${apiBase}/Security/RegisterAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'SiteKey': getSiteKey()
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const result = await response.json();

      if (!result.Success) {
        ToasterService.error(result.ErrorMessage || 'Registration failed. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
        return;
      }

      logger.info('Registration successful', { email, guid: result.GUID });

      const guidBlock = result.GUID
        ? `<p>Your account reference:</p>
           <p class="font-mono bg-base-200 p-3 rounded select-all text-sm break-all">${result.GUID}</p>`
        : '';

      ModalSystem.hide();
      ModalSystem.show({
        title: 'Account Created',
        html: `<div class="text-center space-y-4">
          <p class="text-success font-semibold">Your account has been created successfully!</p>
          ${guidBlock}
          <p class="text-sm text-base-content/60">Please sign in with your new credentials.</p>
        </div>`,
        buttons: [{ caption: 'OK', className: 'btn-primary', callback: () => ModalSystem.hide() }]
      });
    } catch (error: any) {
      logger.error('Registration failed', { error: error.message, stack: error.stack });
      ToasterService.error('Registration failed. Please try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}
